import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Store, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api, tokenStore } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { LoginCaptcha, type CaptchaValue } from '@/features/auth/LoginCaptcha';

/**
 * "Finish connecting your store" — the landing page of a Shopify App Store install.
 *
 * A store that installs from the Shopify listing finishes OAuth with a valid access token and no
 * answer to the only question that matters next: which Gaint account does it belong to? This page
 * asks. Sign in and the store attaches to that account; say you're new and one is made from the
 * store's own details and you're signed straight into it.
 *
 * Guessing that answer instead is what broke order sync: the store was attached to an account the
 * merchant never opened, so orders synced perfectly into a window nobody was looking at.
 */

type Pending =
  | { status: 'ready'; shop: string; shopName?: string | null; shopEmail?: string | null }
  | { status: 'expired' | 'done' | 'unknown'; shop?: string };

export function ConnectStorePage() {
  const handoff = new URLSearchParams(window.location.search).get('handoff') ?? '';
  const setUser = useAuth((s) => s.setUser);

  const [pending, setPending] = useState<Pending | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /* This form signs in through the same endpoint the portal login uses, and the API asks every
     WEB caller for a security code — so this one needs it too, or connecting a store would
     fail with a message about a code that was never on screen. */
  const [captcha, setCaptcha] = useState<CaptchaValue>({ captchaToken: '', captchaAnswer: '' });
  const [captchaKey, setCaptchaKey] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState<'link' | 'new' | null>(null);

  useEffect(() => {
    document.body.classList.add('login-page');
    return () => document.body.classList.remove('login-page');
  }, []);

  useEffect(() => {
    if (!handoff) { setPending({ status: 'unknown' }); setLoading(false); return; }
    api.get(`/shopify/pending/${handoff}`)
      .then((r) => {
        setPending(r.data);
        if (r.data?.shopEmail) setEmail(r.data.shopEmail);
      })
      .catch(() => setPending({ status: 'unknown' }))
      .finally(() => setLoading(false));
  }, [handoff]);

  const finish = (shop: string) => {
    toast.success(`${shop} connected`);
    // Full reload rather than a client-side route change: the nav and the wallet visibility are
    // decided from the merchant profile fetched at boot, and this account may have just become a
    // Shopify (free) account.
    setTimeout(() => { window.location.href = '/channels?shopify=connected'; }, 400);
  };

  /** Sign in, then attach the store to that account. */
  const linkToExisting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter your Gaint email and password'); return; }
    setBusy('link');
    try {
      const login = await api.post('/auth/login/merchant', { email, password, ...captcha });
      tokenStore.set(login.data.accessToken);
      const m = login.data.merchant;
      setUser({
        id: m.id, kind: 'merchant', email: m.email, companyName: m.companyName,
        walletBalance: m.walletBalance, status: m.status, serviceType: m.serviceType,
        emailVerified: m.emailVerified, onboardingStatus: m.onboardingStatus,
      } as any);
      const r = await api.post(`/shopify/pending/${handoff}/link`, {});
      finish(r.data?.shop ?? (pending as any)?.shop ?? 'Your store');
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? 'Could not connect the store');
      setCaptchaKey((k) => k + 1);   // the code is single-use; the one on screen is now spent
      setBusy(null);
    }
  };

  /** No Gaint account yet — make one from the store's own details and sign in. */
  const createAccount = async () => {
    setBusy('new');
    try {
      const r = await api.post(`/shopify/pending/${handoff}/provision`, {});
      // The server hands back a one-time sign-in URL, same mechanism as "Login as merchant".
      window.location.href = r.data.redirectTo;
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? 'Could not create your account');
      setBusy(null);
    }
  };

  const shopLabel = (pending as any)?.shopName || (pending as any)?.shop || 'your Shopify store';

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Store size={22} />
            </div>
            <div>
              <div className="font-semibold text-slate-900 text-lg">Finish connecting your store</div>
              <div className="text-slate-500 text-sm">{loading ? 'Checking your install…' : shopLabel}</div>
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center">
              <Loader2 className="animate-spin" size={16} /> Loading
            </div>
          )}

          {!loading && pending?.status === 'ready' && (
            <>
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 mb-5">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <div>
                  <b>{pending.shop}</b> is installed. Sign in with your Gaint account and we will link the
                  store to it — new orders will then appear in your order list automatically.
                </div>
              </div>

              <form onSubmit={linkToExisting} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Gaint email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="you@yourcompany.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <LoginCaptcha value={captcha} onChange={setCaptcha} reloadKey={captchaKey} />

                <button
                  type="submit"
                  disabled={busy !== null}
                  className="w-full rounded-lg bg-primary text-white py-2.5 text-sm font-medium hover:bg-primary-700 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {busy === 'link' && <Loader2 className="animate-spin" size={15} />}
                  Sign in and connect store
                </button>
              </form>

              <div className="relative my-5 text-center">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <span className="relative bg-white px-3 text-xs uppercase tracking-wide text-slate-400">or</span>
              </div>

              <button
                type="button"
                onClick={createAccount}
                disabled={busy !== null}
                className="w-full rounded-lg border border-slate-300 bg-white text-slate-700 py-2.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy === 'new' && <Loader2 className="animate-spin" size={15} />}
                I'm new — create a Gaint account for this store
              </button>
              <p className="text-xs text-slate-400 mt-3 text-center">
                The Gaint app is free on the Shopify App Store. No payment is taken in the app.
              </p>
            </>
          )}

          {!loading && pending?.status === 'done' && (
            <Notice tone="ok" title="This store is already connected">
              Sign in to the Gaint portal and open <b>Sales Channels</b> to see it.
              <a href="/login" className="block mt-3 text-primary font-medium">Go to sign in →</a>
            </Notice>
          )}

          {!loading && (pending?.status === 'expired' || pending?.status === 'unknown') && (
            <Notice tone="warn" title={pending?.status === 'expired' ? 'This link has expired' : "This link isn't valid"}>
              Open <b>Gaint logistics</b> from your Shopify admin (Settings → Apps and sales channels)
              and we will bring you straight back here with a fresh link.
            </Notice>
          )}
        </div>
      </div>
    </div>
  );
}

function Notice({ tone, title, children }: { tone: 'ok' | 'warn'; title: string; children: React.ReactNode }) {
  const cls = tone === 'ok'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-amber-50 border-amber-200 text-amber-900';
  return (
    <div className={`rounded-lg border p-4 text-sm ${cls}`}>
      <div className="flex items-center gap-2 font-medium mb-1">
        {tone === 'ok' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        {title}
      </div>
      <div className="opacity-90">{children}</div>
    </div>
  );
}
